<script setup lang="ts">
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import DeviceMap from '@/components/device/map/DeviceMap.vue';
import MapControlPanel from '@/components/_common/navbar/MapControlPanel.vue';
import ThreeColumnLayout from '@/components/_common/ThreeColumnLayout.vue';
import GroupDetailsView from '@/views/GroupDetailsView.vue';
import DeviceListBar from '@/components/device/list/DeviceListBar.vue';
import GroupDetailsNavBar from '@/components/device/details/GroupDetailsNavBar.vue';
import { getAccountDevices } from '@gpx-spa/queries';
import { useDevice } from '@/composables/useDevice';

import { useSynchronizedSidebars } from '@/composables/useSynchronizedSidebars';
useSynchronizedSidebars({ defaultOpenSidebar: 'left' });

import { useSynchronizedUrlQuerySearchStore } from '@/composables/useSynchronizedUrlQuerySearchStore';
import { useSynchronizedDeviceStatusQuery } from '@/composables/useSynchronizedDeviceStatusQuery';

useSynchronizedUrlQuerySearchStore();
useSynchronizedDeviceStatusQuery();

import { useResetChatOnSidebarClose } from '@/composables/useResetChatOnSidebarClose';
useResetChatOnSidebarClose();

import { useResetChatOnAccountOrUserChange } from '@/composables/useResetChatOnAccountOrUserChange';
useResetChatOnAccountOrUserChange();

// Hydrate the shared device store with every status so DeviceList can resolve provisioned and
// archived members of the group regardless of which All Trackers tab was last selected. Reuses
// the same TanStack cache key as Trackers.vue, so navigation between the two views is free.
const store = useStore();
const accountId = computed(() => store.getters['auth/accountId']);
const canSeeCancelledDevices = computed(
  () => store.getters['auth/canSeeCancelledDevices'],
);
const deviceStore = useDevice();

const defaultStatuses = 'active,pending,pending-renewal,pending-cancellation';
const subscriptionStatuses = computed(() =>
  canSeeCancelledDevices.value
    ? `${defaultStatuses},cancelled,blocked`
    : defaultStatuses,
);

const queryParams = computed(() => ({
  with_current_position: true,
  subscription_status: subscriptionStatuses.value,
  exclude_groups: true,
  device_status: 'active,provisioned,hidden',
}));

const { data: devicesData } = getAccountDevices(accountId, queryParams);

watch(
  devicesData,
  (newDevices) => {
    if (newDevices) {
      deviceStore.fillStoreDevicesFromResponse(
        deviceStore.devices,
        newDevices,
        true,
      );
      deviceStore.applyFilter();
    }
  },
  { immediate: true },
);
</script>

<template>
  <ThreeColumnLayout>
    <template #leftTop>
      <GroupDetailsNavBar />
    </template>
    <template #left>
      <GroupDetailsView />
    </template>
    <template #rightTop>
      <MapControlPanel>
        <template #filters>
          <DeviceListBar />
        </template>
      </MapControlPanel>
    </template>
    <template #right>
      <DeviceMap :view="`cluster`" :viewType="`groupDetail`" />
    </template>
  </ThreeColumnLayout>
</template>
